/**
 * Run `mapper` over `items` with at most `concurrency` in-flight promises.
 * Results keep input order (like `Promise.all`). Rejects if any mapper rejects.
 */
export async function mapWithConcurrency<T, R>(
  items: readonly T[],
  concurrency: number,
  mapper: (item: T, index: number) => Promise<R>
): Promise<R[]> {
  if (items.length === 0) {
    return []
  }
  const limit = Math.max(1, Math.floor(concurrency))
  const results: R[] = Array.from({ length: items.length })
  let nextIndex = 0

  const workers = Array.from({ length: Math.min(limit, items.length) }, async () => {
    while (true) {
      const index = nextIndex++
      if (index >= items.length) {
        return
      }
      results[index] = await mapper(items[index]!, index)
    }
  })

  await Promise.all(workers)
  return results
}

/**
 * Like {@link mapWithConcurrency}, but settles every item (fulfilled or rejected)
 * instead of failing the whole batch on the first rejection.
 */
export async function mapAllSettledWithConcurrency<T, R>(
  items: readonly T[],
  concurrency: number,
  mapper: (item: T, index: number) => Promise<R>
): Promise<PromiseSettledResult<R>[]> {
  return mapWithConcurrency(items, concurrency, async (item, index) => {
    try {
      const value = await mapper(item, index)
      return { status: 'fulfilled' as const, value }
    }
    catch (reason) {
      return { status: 'rejected' as const, reason }
    }
  })
}
